package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.Testimonials
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

// Real user testimonials. Text-only cards (no name / rating / avatar).
// The copy lives in :shared (Testimonials.REVIEWS) because mobile renders the same belt and the two
// UI systems can't share composables — a local copy here would drift the moment either is edited.
private val REVIEWS = Testimonials.REVIEWS

/**
 * A horizontally auto-scrolling "conveyor belt" of user reviews (quote text only — no name,
 * rating, or avatar). The track holds two identical passes of [REVIEWS] and loops seamlessly by
 * wrapping the translate every half-track. The scroll is JS-driven (requestAnimationFrame) rather
 * than a CSS animation so it supports drag: press to pause, drag horizontally to browse, release to
 * resume. It pauses on mouse-hover and respects `prefers-reduced-motion`. Full-bleed (100vw).
 */
@Composable
fun ReviewsBelt() {
    InjectReviewsBeltStyles()

    Div(attrs = { classes("reviews-section") }) {
        Div(attrs = { classes("reviews-label") }) { Text("HOW OUR USERS FEEL") }
        Div(attrs = { classes("reviews-belt") }) {
            Div(attrs = {
                classes("reviews-track")
                ref { el ->
                    val cleanup = setupReviewsMarquee(el.unsafeCast<HTMLElement>())
                    onDispose { cleanup() }
                }
            }) {
                // Two identical passes so wrapping the translate by half the track loops seamlessly.
                repeat(2) {
                    REVIEWS.forEach { text ->
                        Div(attrs = { classes("review-card") }) {
                            P(attrs = { classes("review-text") }) { Text("“$text”") }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Drives the marquee's horizontal scroll in JS so it can be dragged. Auto-advances each frame at
 * the same speed the old CSS animation used (half the track over 82.5s). Pointer press pauses and
 * lets the user drag the track; release resumes. Mouse hover pauses (matching the desktop feel);
 * touch does NOT pause on hover so releasing a drag resumes immediately. Returns a cleanup fn.
 */
private fun setupReviewsMarquee(track: HTMLElement): () -> Unit {
    var offset = 0.0        // current translateX (px), continuous
    var halfWidth = 0.0     // width of one REVIEWS pass (= scrollWidth / 2)
    var hovering = false    // mouse hover (desktop) → pause
    var pressing = false    // pointer down → pause + drag
    var startX = 0.0
    var startOffset = 0.0
    var lastTs = 0.0
    var rafId = 0
    val reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    fun wrap(x: Double): Double {
        if (halfWidth <= 0.0) return x
        var v = x % halfWidth
        if (v > 0.0) v -= halfWidth   // keep in (-halfWidth, 0]
        return v
    }

    fun frame(ts: Double) {
        if (halfWidth <= 0.0) halfWidth = track.scrollWidth / 2.0
        // Cap dt so returning from a backgrounded tab (rAF was paused) doesn't jump the marquee.
        var dt = if (lastTs == 0.0) 0.0 else ts - lastTs
        if (dt > 100.0) dt = 100.0
        lastTs = ts
        val paused = hovering || pressing || reduceMotion
        if (!paused && halfWidth > 0.0) offset -= (halfWidth / 82500.0) * dt
        if (!pressing && halfWidth > 0.0) offset = wrap(offset)  // keep bounded when not dragging
        track.style.setProperty("transform", "translateX(" + wrap(offset) + "px)")
        rafId = window.requestAnimationFrame(::frame)
    }
    rafId = window.requestAnimationFrame(::frame)

    val onEnter: (Event) -> Unit = { e -> if (e.asDynamic().pointerType == "mouse") hovering = true }
    val onLeave: (Event) -> Unit = { e -> if (e.asDynamic().pointerType == "mouse") hovering = false }
    val onDown: (Event) -> Unit = { e ->
        pressing = true
        startX = (e as MouseEvent).clientX.toDouble()
        startOffset = offset
    }
    val onMove: (Event) -> Unit = { e ->
        if (pressing) offset = startOffset + ((e as MouseEvent).clientX.toDouble() - startX)
    }
    val onUp: (Event) -> Unit = { pressing = false; lastTs = 0.0 }

    track.addEventListener("pointerenter", onEnter)
    track.addEventListener("pointerleave", onLeave)
    track.addEventListener("pointerdown", onDown)
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)

    return {
        window.cancelAnimationFrame(rafId)
        track.removeEventListener("pointerenter", onEnter)
        track.removeEventListener("pointerleave", onLeave)
        track.removeEventListener("pointerdown", onDown)
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
        window.removeEventListener("pointercancel", onUp)
    }
}

/**
 * Injects the belt's CSS exactly once via a real <style> element. We use direct DOM injection
 * (not Compose HTML's `Style {}` builder) because the browser parses <style>.textContent with its
 * full CSS engine — Compose HTML's naive string parser drops blocks containing nested @media,
 * vendor-prefixed props, or multi-comma values like mask-image gradients.
 */
@Composable
private fun InjectReviewsBeltStyles() {
    LaunchedEffect(Unit) {
        val styleId = "reviews-belt-styles"
        val doc = kotlinx.browser.document
        if (doc.getElementById(styleId) != null) return@LaunchedEffect
        val style = doc.createElement("style") as org.w3c.dom.HTMLStyleElement
        style.id = styleId
        style.textContent = """
            .reviews-section { width: 100%; }
            .reviews-label {
                text-align: center;
                text-transform: uppercase;
                font-size: 11px;
                font-weight: 600;
                letter-spacing: 3px;
                color: rgba(255,255,255,0.5);
                /* Sits close to the belt on purpose. The cards are a fixed 92px with their quote
                   vertically centred, so they already carry empty space at the top — anything
                   larger here reads as a gap between the heading and what it labels. */
                margin-bottom: 2px;
            }
            .reviews-belt {
                width: 100vw;
                margin-left: calc(50% - 50vw);
                /* clip horizontally (the marquee), but let a hovered testimonial's pop spill
                   out vertically toward the viewer */
                overflow-x: clip;
                overflow-y: visible;
                padding: 4px 0;
                opacity: 0.65;
                transition: opacity 0.4s ease;
                -webkit-mask-image: linear-gradient(to right, transparent 0, #000 7%, #000 93%, transparent 100%);
                mask-image: linear-gradient(to right, transparent 0, #000 7%, #000 93%, transparent 100%);
                /* fade horizontally, but DON'T let the mask clip a hovered card's vertical pop-out */
                -webkit-mask-clip: no-clip;
                mask-clip: no-clip;
            }
            /* Hovering anywhere on the strip pauses it and brings it to full opacity... */
            .reviews-belt:hover { opacity: 1; }
            .reviews-track {
                display: flex;
                width: max-content;
                will-change: transform;
                cursor: grab;
                /* let vertical page scroll pass through; we handle horizontal drag in JS */
                touch-action: pan-y;
                user-select: none;
                -webkit-user-select: none;
            }
            .reviews-track:active { cursor: grabbing; }
            .review-card {
                flex: 0 0 auto;
                width: 300px;
                height: 92px;
                margin: 0 16px;
                box-sizing: border-box;
                position: relative;
                transform-origin: center center;
                /* no card chrome now — just the floating quote.
                   align-items: flex-start (not center) because the 92px is sized for the longest
                   quote at 4 lines; centring left short quotes floating down from the card's top
                   edge, and that slack read as a gap under the heading that sits right above the
                   belt. Top-aligning starts every quote on the same line; the ragged edge lands at
                   the bottom where nothing is anchored to it. */
                display: flex;
                align-items: flex-start;
                justify-content: center;
                /* hover-OUT: smooth settle back, no bounce */
                transition: transform 0.3s ease;
            }
            /* the single testimonial under the cursor gently pops forward with a springy
               overshoot (y2 = 2.0). */
            .review-card:hover {
                transform: scale(1.15);
                z-index: 5;
                transition: transform 0.35s cubic-bezier(0.34, 2.0, 0.5, 1);
            }
            .review-text {
                font-size: 14px;
                color: rgba(255,255,255,0.95);
                line-height: 1.45;
                margin: 0;
                font-style: italic;
                text-align: center;
                text-shadow: 0 1px 3px rgba(0,0,0,0.28);
                display: -webkit-box;
                -webkit-line-clamp: 4;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }
        """.trimIndent()
        doc.head?.appendChild(style)
    }
}
